import React, { useState, useMemo } from "react";

import { Grid } from "@mui/material";
import { Skeleton } from "@mui/material";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../context/StateContext";
import { useStyles } from "./styles";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

type LatLng = {
  lat: number;
  lng: number;
};

export type VoyageMapInformationProps = {
  vesselName?: string;
  position?: LatLng;
  lastUpdate?: Date | string;
  speed?: string;
};

type VoyageMapProps = {
  information?: VoyageMapInformationProps;
  loadingMap?: boolean;
  showLivePosition?: boolean;
};

const DATE_FORMAT = "dd/MM/yyyy hh:mm aa";

const MARKER_ICON = require("../../../assets/img/ship-marker.svg");

const ZOOM_MAP = 3;

/**
 * Component to render a map with marker, showing the informations (vessel name, latitude, longitude) when click on marker
 * @param information  - Vessel name and position of Latitude and Longitude to  set map position of center and show on modal when click on marker
 * @returns Map JSX Element with voyage props
 */
export const VoyageMap: React.FC<VoyageMapProps> = ({
  information,
  loadingMap = true,
  showLivePosition = true,
  children,
}) => {
  const classes = useStyles();

  const { mapIsLoaded } = useAppState();

  const [informationWindowOpened, setInformationWindowOpened] = useState(false);

  const showMap = useMemo(() => !loadingMap && mapIsLoaded, [loadingMap, mapIsLoaded]);

  const renderMap = () => {
    if (information && information.position && showMap) {
      return (
        <div className={classes.map}>
          <GoogleMap
            center={information.position}
            zoom={ZOOM_MAP}
            options={{
              minZoom: 2,
              maxZoom: 15,
            }}
            mapContainerClassName={classes.map}
          >
            {informationWindowOpened && information && (
              <InfoWindow
                position={information.position}
                onCloseClick={() => setInformationWindowOpened(false)}
              >
                <InfoWindowContent data={information} />
              </InfoWindow>
            )}
            {showLivePosition ? (
              <Marker
                icon={{
                  url: MARKER_ICON,
                  anchor: new google.maps.Point(1, 5),
                  scaledSize: new google.maps.Size(20, 20),
                }}
                position={information.position}
                onClick={() => setInformationWindowOpened(true)}
              ></Marker>
            ) : null}
            {children}
          </GoogleMap>
        </div>
      );
    }
  };

  return (
    <Grid container direction="column">
      {!showMap && <Skeleton className={classes.skeleton} />}
      {renderMap()}
    </Grid>
  );
};

type InfoWindowContentProps = {
  data: VoyageMapInformationProps;
};

const InfoWindowContent = ({ data }: InfoWindowContentProps) => {
  const { vesselName, position, lastUpdate, speed } = data;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const date = lastUpdate
    ? lastUpdate instanceof Date
      ? formatDateWithUserPref(lastUpdate, currentUser!.preferences!)
      : formatDateWithUserPref(new Date(lastUpdate), currentUser!.preferences!)
    : "";

  const speedConverted = useMemo(
    () => (speed && !Number.isNaN(Number(speed)) ? Number(speed) * 10 : ""),
    [speed]
  );

  return (
    <Grid container direction="column" className={classes.windowContainer}>
      <Grid container direction="row" alignItems="center" className={classes.windowContent}>
        <Grid item className={classes.title}>
          {translate("vesselName")}:{" "}
        </Grid>
        <Grid item className={classes.description}>
          {vesselName}
        </Grid>
      </Grid>
      <Grid container direction="row" alignItems="center" className={classes.windowContent}>
        <Grid item className={classes.title}>
          {translate("latitude")}:{" "}
        </Grid>
        <Grid item className={classes.description}>
          {position?.lat}
        </Grid>
      </Grid>
      <Grid container direction="row" alignItems="center" className={classes.windowContent}>
        <Grid item className={classes.title}>
          {translate("longitude")}:{" "}
        </Grid>
        <Grid item className={classes.description}>
          {position?.lng}
        </Grid>
      </Grid>
      {speedConverted && (
        <Grid container direction="row" alignItems="center" className={classes.windowContent}>
          <Grid item className={classes.title}>
            {translate("speedKnots")}:{" "}
          </Grid>
          <Grid item className={classes.description}>
            {speedConverted}
          </Grid>
        </Grid>
      )}
      {date && (
        <Grid container direction="row" alignItems="center" className={classes.windowContent}>
          <Grid item className={classes.title}>
            {translate("lastUpdate")}:{" "}
          </Grid>
          <Grid item className={classes.description}>
            {date} (UTC)
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
